import {CloudUpload} from "@mui/icons-material";
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled, Typography} from "@mui/material";
import {forwardRef, useImperativeHandle, useState} from "react";
import {useRetailLocationImport} from "../../hooks/useRetailLocationImport";
import VisuallyHiddenInput from "../../components/VisuallyHiddenInput";
import {useRetailLocationValidateImport} from "../../hooks/useRetailLocationValidateImport";

const RetailLocationImportModal = forwardRef(({refetchRetailLocations}, ref) => {
    const [open, setOpen] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);
    const retailLocationImportMutation = useRetailLocationImport();
    const retailLocationValidateImportMutation = useRetailLocationValidateImport();
    useImperativeHandle(
        ref,
        () => ({
            handleOpen,
        }),
        []
    );

    const [selectedFile, setSelectedFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const handleOpen = () => {
        resetState();
        setOpen(true);
    };

    const handleClose = () => {
        resetState();
        setOpen(false);
    };
    const resetState = () => {
        setSelectedFile(null);
        setIsVerified(false);
        setIsImporting(false);
        resetMessages();
    };
    const resetMessages = () => {
        setErrorMessages([]);
        setSuccessMessage("");
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        resetMessages();
        if (file && file.type === "text/csv") {
            setSelectedFile(file);
            setErrorMessages([]);
        } else {
            setSelectedFile(null);
            setErrorMessages(["Please upload a valid CSV file."]);
        }
    };
    const onValidateSuccess = () => {
        setIsVerified(true);
        setIsVerifying(false);
        setSuccessMessage("File validation successful.");
    };
    const onValidateFail = ({response}) => {
        setIsVerifying(false);
        const {data} = response;
        const keys = Object.keys(data);
        const errors = [];
        for (const k of keys) {
            errors.push(`<b>Line ${k}:</b> ${data[k].join(", ")}`);
        }
        setErrorMessages(errors);
    };
    const handleVerify = () => {
        setIsVerifying(true);
        retailLocationValidateImportMutation.mutate(selectedFile, {
            onSuccess: onValidateSuccess,
            onError: onValidateFail,
        });
    };
    const onImportSuccess = (response) => {
        setSuccessMessage(`Import completed: Created: ${response.created}, Updated: ${response.updated}`);
        setSelectedFile(null);
        setIsImporting(false);
        refetchRetailLocations();
    };
    const onImportFail = (error) => {
        setErrorMessages([error?.message || "Failed to import the file. Please try again."]);
        setIsImporting(false);
    };
    const handleUpload = async () => {
        if (!selectedFile) {
            setErrorMessages("No file selected. Please upload a CSV file.");
            return;
        }
        setSuccessMessage();
        setIsImporting(true);
        retailLocationImportMutation.mutate(selectedFile, {
            onSuccess: onImportSuccess,
            onError: onImportFail,
        });
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Import Retail Locations</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Please select a CSV file from which to import data. The format of the file much match that of
                        csv file from <b>EXPORT RETAIL LOCATIONS</b> (e.g., no space characters after comma delimiter)
                    </Typography>
                    <Box
                        display={"flex"}
                        flexDirection={"column"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        mt={2}
                    >
                        <Button
                            component="label"
                            role={undefined}
                            variant="contained"
                            tabIndex={-1}
                            startIcon={<CloudUpload />}
                        >
                            SELECT FILE
                            <VisuallyHiddenInput type="file" accept=".csv" onChange={handleFileChange} multiple />
                        </Button>
                        {selectedFile && (
                            <Typography variant="body2" sx={{mt: 2}}>
                                Selected file: {selectedFile.name}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        onClick={handleVerify}
                        variant="contained"
                        color="primary"
                        disabled={!selectedFile || isVerifying}
                    >
                        {isVerifying ? "Verifying..." : "Verify"}
                    </Button>
                    <Button
                        onClick={handleUpload}
                        variant="contained"
                        color="primary"
                        disabled={!isVerified || !selectedFile || isImporting}
                    >
                        {isImporting ? "Importing..." : "Import"}
                    </Button>
                </DialogActions>
                {errorMessages.length > 0 && (
                    <Alert severity="error" sx={{mt: 2, maxHeight: 300, overflowY: "auto"}}>
                        {errorMessages.map((error, index) => (
                            <Typography
                                key={index}
                                variant="body2"
                                dangerouslySetInnerHTML={{__html: error}}
                            ></Typography>
                        ))}
                    </Alert>
                )}
                {successMessage && (
                    <Alert severity="success" sx={{mt: 2}}>
                        {successMessage}
                    </Alert>
                )}
            </Dialog>
        </>
    );
});

export default RetailLocationImportModal;
